import { HelperFunctions } from "@/lib/utilities/HelperFunctions";
import { Permissions, FeatureFlags } from "@/lib/utilities/enums";
import store from "@/store";

export function buildNamedRoutes(baseRouteObj, parent = {}) {
  for (const [name, value] of Object.entries(baseRouteObj)) {
    value.meta = { ...(parent.meta || {}), ...(value.meta || {}) };
    value.name = parent.name ? [parent.name, name].join("/") : name;
    value.path = parent.path ? [parent.path, value.path].join("/") : value.path;
    value.meta.routeObj = { name: value.name };

    if (
      value.meta.activeProductFeatures &&
      value.meta.activeProductFeatures.length > 0
    ) {
      value.meta.hasFeature = () => {
        return !value.meta.activeProductFeatures.some(
          featureId => !store.getters.ViewActiveFeatureIds.includes(featureId)
        );
      };
    }

    if (value.meta.permissions || value.meta.requiresRootAccount) {
      value.meta.hasPermission = user => {
        if (
          !user ||
          !Array.isArray(user.Permissions) ||
          user.Permissions.length === 0
        ) {
          console.warn("Did not have valid user, rejecting...");
          return false;
        }
        if (value.meta.requiresRootAccount && user.AccountID != 1) {
          return false;
        }
        if (
          Array.isArray(value.meta.permissions) &&
          !HelperFunctions.hasPermission(user, value.meta.permissions)
        ) {
          //console.log("no permission " + value.meta.permissions);
          return false;
        }
        return true;
      };
    }

    value.beforeEnter = (to, from, next) => {
      let timeOutTime =
        !store.getters.User ||
        !store.getters.ViewAccountProducts ||
        store.getters.ViewAccountProducts.length == 0
          ? 2000
          : 0;
      setTimeout(() => {
        if (
          (typeof value.meta.hasPermission !== "function" ||
            value.meta.hasPermission(store.getters.User)) &&
          (typeof value.meta.hasFeature !== "function" ||
            value.meta.hasFeature())
        ) {
          next();
        } else {
          // console.error("No permission, redirecting to noaccess.");
          next(NamedRoutes.NoAccess.meta.routeObj);
        }
      }, timeOutTime);
    };
    if (value.children)
      value.children = buildNamedRoutes(value.children, value);
  }
  return baseRouteObj;
}

export function buildRoutesObject() {
  const out = [];
  for (const routeGroup of Object.values(NamedRoutes)) {
    if (!routeGroup.meta.customRouterHandling) {
      if (routeGroup.children) {
        const children = Object.values(routeGroup.children).filter(
          routeChild => !routeChild.meta.customRouterHandling
        );
        out.push(...children);
        out.push({
          name: routeGroup.name,
          path: routeGroup.path,
          redirect: () => {
            const sideNavGroup = store.getters.SideNavMenuItems.find(
              navGroup => navGroup.name == routeGroup.name
            );
            if (
              !sideNavGroup ||
              !Array.isArray(sideNavGroup.children) ||
              sideNavGroup.children.length == 0
            ) {
              console.error(
                "Redirecting to NoAccess: No matching sideNavGroup"
              );
              console.error(routeGroup);
              return NamedRoutes.NoAccess.meta.routeObj;
            } else {
              return sideNavGroup.children[0].meta.routeObj;
            }
          }
        });
      } else {
        if (!routeGroup.component && !routeGroup.redirect) {
          console.warn(
            `No route handler found for ${routeGroup.name}, adding anyway.`
          );
        }
        out.push(routeGroup);
      }
    }
  }
  return out;
}

export const NamedRoutes = buildNamedRoutes({
  NoAccess: {
    path: "noaccess",
    component: () => import("@/components/pages/NoAccess"),
    meta: {
      sideNav: false
    }
  },
  SystemAdmin: {
    path: "systemadmin",
    meta: {
      sideNavIcon: "gear",
      sideNavGroupLabel: "System Admin",
      requiresRootAccount: true
    },
    children: {
      PackageAdmin: {
        path: "packageadmin",
        component: () =>
          import("@/components/pages/PackageConfig/PackageConfig"),
        meta: {
          sideNavLabel: "Package Admin",
          permissions: [Permissions.Manage_Packages]
        }
      },
      ProductAdmin: {
        path: "productadmin",
        component: () => import("@/components/pages/SystemAdmin/ProductAdmin"),
        meta: {
          sideNavLabel: "Product Admin",
          permissions: [Permissions.Developer_Admin]
        }
      },
      Roles: {
        path: "roles",
        component: () => import("@/components/pages/SystemAdmin/Roles"),
        meta: {
          sideNavLabel: "Roles",
          permissions: [Permissions.Developer_Admin, Permissions.Manage_Roles]
        }
      },
      Permissions: {
        path: "permissions",
        component: () => import("@/components/pages/SystemAdmin/Permissions"),
        meta: {
          sideNavLabel: "Permissions",
          permissions: [Permissions.Developer_Admin]
        }
      },
      SisenseManager: {
        path: "sisensemanager",
        component: () =>
          import(
            "@/components/pages/SystemAdmin/SisenseManager/SisenseManager"
          ),
        meta: {
          sideNavLabel: "Sisense Dashboard Manager",
          permissions: [Permissions.SisenseDashboardManager]
        }
      },
      TableauManager: {
        path: "tableaumanager",
        component: () =>
          import(
            "@/components/pages/SystemAdmin/TableauManager/TableauManager.vue"
          ),
        meta: {
          sideNavLabel: "Tableau Manager",
          permissions: [Permissions.TableauViewManager]
        }
      },
      WebsiteVendors: {
        path: "websitevendors",
        component: () =>
          import("@/components/pages/SystemAdmin/WebsiteVendors"),
        meta: {
          sideNavLabel: "Website Vendors",
          permissions: [
            Permissions.Developer_Admin,
            Permissions.System_WebsiteVendor_View
          ]
        }
      },
      SystemConfiguration: {
        path: "systemconfiguration",
        component: () =>
          import("@/components/pages/SystemAdmin/SystemConfiguration"),
        meta: {
          sideNavLabel: "System Configuration",
          permissions: [Permissions.Developer_Admin]
        }
      },
      EmailTemplate: {
        path: "emailTemplate",
        component: () => import("@/components/pages/SystemAdmin/EmailTemplate"),
        meta: {
          sideNavLabel: "Email Templates",
          permissions: [Permissions.Developer_Admin]
        }
      },
      RulesAdmin: {
        path: "settings",
        component: () =>
          import("@/components/pages/SystemAdmin/RulesAdmin/DealerSettings"),
        meta: {
          sideNavLabel: "Workflow Settings",
          activeProductFeatures: [FeatureFlags.CXM],
          permissions: [
            Permissions.RulesAdministration,
            Permissions.RulesViewOnly
          ]
        }
      },
      SystemTouchpoints: {
        path: "systemTouchpoints",
        component: () =>
          import("@/components/pages/SystemAdmin/SystemTouchpoints"),
        meta: {
          sideNavLabel: "System Touchpoints",
          activeProductFeatures: [FeatureFlags.CXM],
          permissions: [Permissions.Manage_System_Touchpoints]
        }
      },
      OrchestrationEngine: {
        path: "orchestrationengine",
        component: () =>
          import(
            "@/components/pages/SystemAdmin/OrchestrationEngine/OrchestrationEngine"
          ),
        meta: {
          sideNavLabel: "Orchestration Engine",
          permissions: [Permissions.Orchestration_Engine],
          requiresRootAccount: true
        }
      }
    }
  },
  DealerAdmin: {
    path: "dealeradmin",
    meta: {
      sideNavIcon: "stores",
      sideNavGroupLabel: "Dealer Admin"
    },
    children: {
      AccountPlatformDomainBlacklist: {
        path: "AccountPlatformDomainBlacklist",
        component: () =>
          import(
            "@/components/pages/DealerAdmin/AccountPlatformDomainBlacklist"
          ),
        meta: {
          sideNavLabel: "Account Platform Domain Blacklist",
          // Why does dealeradmin have developer admin??
          //      Satish: This is NOT a product page (eg. system admin), but is only for internal use (eg. Ops)
          requiresRootAccount: true,
          permissions: [Permissions.System_DealerDomain_Blacklist]
        }
      },
      DealerWebsites: {
        path: "dealerwebsites",
        component: () =>
          import("@/components/pages/DealerAdmin/DealerWebsites"),
        meta: {
          sideNavLabel: "Dealer Websites",
          // Why does dealeradmin have developer admin??
          //      Satish: This is NOT a product page (eg. system admin), but is only for internal use (eg. Ops)
          requiresRootAccount: true,
          permissions: [Permissions.System_DealerDomain_Edit]
        }
      },
      CallTracking: {
        path: "calltracking",
        component: () =>
          import("@/components/pages/CallTracking/CallTrackingSettings"),
        meta: {
          sideNavLabel: "Call Tracking Settings",
          activeProductFeatures: [FeatureFlags.CallTracking],
          permissions: [Permissions.ManageCallTracking]
        }
      },
      // DealerFeatureConfiguration: {
      //   path: "dfconfig",
      //   component: () =>
      //     import("@/components/pages/DealerAdmin/DealerFeatureConfiguration"),
      //   meta: {
      //     sideNavLabel: "Dealer Feature Configuration",
      //     permissions: [Permissions.O2O_Activation_Edit],
      //     activeProductFeatures: [FeatureFlags.OnlineOffer]
      //   }
      // },
      SocialAccounts: {
        path: "socialAccounts",
        component: () =>
          import("@/components/pages/DealerAdmin/SocialAccounts"),
        meta: {
          sideNavLabel: "Social Accounts"
          // Why does dealeradmin have developer admin??
          //      Satish: This page is not ready for public access
          //permissions: [Permissions.Developer_Admin]
        }
      },
      AssignmentSettings: {
        path: "assignmentSettings",
        component: () =>
          import("@/components/pages/DealerAdmin/AssignmentSettings"),
        meta: {
          sideNavLabel: "Assignment Settings",
          permissions: [Permissions.Manage_Assignments]
        }
      },
      CrmImport: {
        path: "crmImport",
        component: () => import("@/components/pages/DealerAdmin/CrmImport"),
        meta: {
          sideNavLabel: "CRM Import",
          activeProductFeatures: [FeatureFlags.CXM],
          permissions: [Permissions.Crm_Import]
        }
      },
      Integrations: {
        path: "integrations",
        component: () => import("@/components/pages/DealerAdmin/Integrations"),
        meta: {
          sideNavLabel: "Integrations",
          permissions: [Permissions.Manage_Integration_Settings]
        }
      },
      DealerSettings: {
        path: "dealerSettings",
        component: () =>
          import("@/components/pages/DealerAdmin/DealerSettings"),
        meta: {
          sideNavLabel: "Dealer Settings",
          permissions: null //[Permissions.Manage_Integration_Settings]
        }
      }
    }
  },
  AlertMinerSettings: {
    path: "alertminer-settings",
    component: () => import("@/components/pages/AlertMiner/AlertMinerSettings"),
    meta: {
      sideNavIcon: "gear",
      sideNavGroupLabel: "AlertMiner Settings",
      permissions: [Permissions.View_AlertMiner_Settings]
    }
  },
  Users: {
    path: "users",
    meta: {
      sideNavIcon: "system-admin",
      sideNavGroupLabel: "Users"
    },
    children: {
      UserList: {
        path: "list",
        component: () => import("@/components/pages/Users/UserList"),
        meta: {
          sideNavLabel: "Manage Users",
          permissions: [
            Permissions.System_UserList_Edit,
            Permissions.System_UserList_View
          ]
        }
      },
      UserIntegrity: {
        path: "/userintegrity",
        component: () => import("@/components/pages/Users/UserIntegrity"),
        meta: {
          sideNavLabel: "User Setup Verification",
          requiresRootAccount: true,
          permissions: [
            Permissions.Developer_Admin,
            Permissions.UserSetupVerification
          ]
        }
      },
      BulkUserUpload: {
        path: "bulk",
        component: () => import("@/components/pages/Users/BulkUserUpload"),
        meta: {
          sideNavLabel: "Bulk User Upload",
          permissions: [Permissions.System_UserList_Edit]
        }
      },
      // Scheduler: {
      //   path: "scheduler",
      //   component: () =>
      //     import(
      //       "@/components/pages/Users/scheduler/TeamSchedule/TeamSchedule"
      //     ),
      //   meta: {
      //     sideNavLabel: "Team Schedule",
      //     permissions: []
      //   }
      // },
      //  SchedulerSettings: {
      //    path: "scheduler-settings",
      //    component: () =>
      //      import(
      //        "@/components/pages/Users/scheduler/Settings/ScheduleSettingsOLD"
      //      ),
      //    meta: {
      //      sideNavLabel: "Schedule Settings",
      //      permissions: [Permissions.WorkscheduleAdmin]
      //    }
      //  },
      WorkSchedule: {
        path: "work-schedule",
        component: () =>
          import(
            "@/components/pages/Users/scheduler/Settings/WorkScheduleSettings"
          ),
        meta: {
          sideNavLabel: "Work Schedule",
          permissions: []
        }
      }
    }
  },
  Groups: {
    path: "groups",
    meta: {
      sideNavIcon: "group-2",
      sideNavGroupLabel: "Groups"
    },
    children: {
      GroupList: {
        path: "grouplist",
        component: () => import("@/components/pages/Groups/GroupList"),
        meta: {
          sideNavLabel: "Manage Groups",
          permissions: [Permissions.Add_People_To_Group]
        }
      }
    }
  },
  LeadSettings: {
    path: "leads",
    meta: {
      sideNavIcon: "gear",
      sideNavGroupLabel: "Lead Settings",
      activeProductFeatures: [FeatureFlags.ILM]
    },
    children: {
      Settings: {
        path: "settings",
        component: () => import("@/components/pages/LeadSettings/LeadSettings"),
        props: { selectedTab: 1 },
        meta: {
          sideNavLabel: "Settings",
          permissions: [Permissions.Manage_LeadSettings]
        }
      },
      Sources: {
        path: "sources",
        component: () => import("@/components/pages/LeadSettings/LeadSettings"),
        props: { selectedTab: 2 },
        meta: {
          sideNavLabel: "Sources",
          permissions: [Permissions.Manage_LeadSettings]
        }
      },
      Internet: {
        path: "internet",
        component: () => import("@/components/pages/LeadSettings/LeadSettings"),
        props: { selectedTab: 3 },
        meta: {
          sideNavLabel: "Internet",
          permissions: [Permissions.Manage_LeadSettings]
        }
      }
    }
  },
  ExperienceManager: {
    path: "experiencemanager",
    component: () => import("@/components/pages/ExperienceManager/Settings"),
    meta: {
      sideNavIcon: "gear",
      sideNavGroupLabel: "Experience Manager",
      permissions: [Permissions.Manage_Showroom_Activity_Settings],
      activeProductFeatures: [FeatureFlags.ILM, FeatureFlags.ExperienceManager]
    }
  },

  Forms: {
    path: "forms",
    meta: {
      sideNavIcon: "form",
      sideNavGroupLabel: "Forms",
      activeProductFeatures: [FeatureFlags.ILM]
    },
    children: {
      DealerForms: {
        path: "dealerforms",
        component: () => import("@/components/pages/Forms/FormsGallery"),
        props: { selectedTab: 1 },
        meta: {
          sideNavLabel: "Forms",
          permissions: [Permissions.Manage_Forms]
        }
      },
      Packets: {
        path: "packets",
        component: () => import("@/components/pages/Forms/FormsGallery"),
        props: { selectedTab: 2 },
        meta: {
          sideNavLabel: "Packets",
          permissions: [Permissions.Manage_Forms]
        }
      },
      SystemLibrary: {
        path: "systemlibrary",
        component: () => import("@/components/pages/Forms/FormsGallery"),
        props: { selectedTab: 3 },
        meta: {
          sideNavLabel: "System Library",
          permissions: [Permissions.Manage_Forms]
        }
      },
      PacketEditor: {
        path: "packeteditor",
        component: () => import("@/components/pages/Forms/PacketEditor"),
        props: true,
        meta: {
          permissions: [Permissions.Manage_Forms]
        }
      },
      FormWizard: {
        path: "formwizard",
        component: () => import("@/components/pages/Forms/FormWizard"),
        props: true,
        meta: {
          permissions: [Permissions.Manage_Forms]
        }
      }
    }
  },
  Templates: {
    path: "templates",
    meta: {
      sideNavIcon: "form",
      iconFromStage: true,
      sideNavGroupLabel: "Templates"
    },
    children: {
      EditTemplate: {
        path: "edit",
        component: () => import("@/components/pages/Templates/EditTemplate"),
        props: true,
        meta: {
          permissions: [Permissions.ManageTemplates]
        }
      },
      Browse: {
        path: "browse",
        component: () => import("@/components/pages/Templates/Templates"),
        props: ({ query }) => ({ queryCategory: query.category }),
        meta: {
          sideNavEntriesGetter: "SideNavTemplateEntries",
          permissions: [Permissions.ManageTemplates]
        }
      }
    }
  },
  DMSPush: {
    path: "dmspush",
    component: () => import("@/components/pages/DMSPush/DMSPushSettings"),
    meta: {
      sideNavIcon: "gear",
      sideNavGroupLabel: "DMS Push Settings",
      permissions: [Permissions.Dmspush_Admin]
    }
  },
  VideoLibrary: {
    path: "videoLibrary",
    component: () => import("@/components/pages/VideoLibrary/VideoLibrary.vue"),
    meta: {
      activeProductFeatures: [FeatureFlags.VideoMessaging],
      sideNavIcon: "video-reel",
      sideNavGroupLabel: "Video Library",
      iconFromStage: true
    }
  }
});



// WEBPACK FOOTER //
// ./src/lib/utilities/helpers/navigation.js